import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Loader2, CheckCircle2, XCircle, MinusCircle, Clock, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABEL = {
  queued: 'Queued',
  processing: 'Sending…',
  completed: 'Completed',
  completed_with_errors: 'Completed with errors',
};

// Office-facing view of a durable day-before reminder batch.
// Polls the backend while the batch is active — the operator can close
// this and re-open later; the batch keeps processing server-side.
export const ReminderBatchPanel = ({ open, onClose, batchId, dateLabel }) => {
  const [batch, setBatch] = useState(null);
  const [messages, setMessages] = useState([]);
  const [retrying, setRetrying] = useState(false);

  const fetchBatch = useCallback(async () => {
    if (!batchId) return;
    try {
      const token = await getAuth().currentUser?.getIdToken();
      const res = await axios.get(
        `${API}/api/notifications/visit-reminders/batches/${batchId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setBatch(res.data.batch);
      setMessages(res.data.messages || []);
    } catch (err) {
      console.error('[ReminderBatchPanel] fetch error:', err);
    }
  }, [batchId]);

  useEffect(() => {
    if (!open || !batchId) return;
    fetchBatch();
    const interval = setInterval(() => {
      setBatch((b) => {
        if (b && (b.status === 'completed' || b.status === 'completed_with_errors')) return b;
        fetchBatch();
        return b;
      });
    }, 2500);
    return () => clearInterval(interval);
  }, [open, batchId, fetchBatch]);

  const handleRetryFailed = async () => {
    setRetrying(true);
    try {
      const token = await getAuth().currentUser?.getIdToken();
      const res = await axios.post(
        `${API}/api/notifications/visit-reminders/batches/${batchId}/retry-failed`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`${res.data.requeued} message${res.data.requeued !== 1 ? 's' : ''} requeued`);
      fetchBatch();
    } catch (err) {
      console.error('[ReminderBatchPanel] retry error:', err);
      toast.error('Failed to requeue messages');
    } finally {
      setRetrying(false);
    }
  };

  const counts = batch?.counts || {};
  const done = (counts.sent || 0) + (counts.skipped || 0) + (counts.failed || 0);
  const total = counts.total || 0;
  const active = batch && (batch.status === 'queued' || batch.status === 'processing');
  const failed = messages.filter((m) => m.status === 'failed');

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="reminder-batch-panel">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {active
              ? <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
              : batch?.status === 'completed'
                ? <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                : <XCircle className="h-4 w-4 text-red-500" />}
            Day-Before Reminders{dateLabel ? ` — ${dateLabel}` : ''}
          </DialogTitle>
          <DialogDescription data-testid="reminder-batch-status">
            {batch ? (STATUS_LABEL[batch.status] || batch.status) : 'Loading…'}
            {active && ' — you can close this; sending continues in the background.'}
            {batch?.finishedAt && ` Finished ${new Date(batch.finishedAt).toLocaleTimeString()}.`}
          </DialogDescription>
        </DialogHeader>

        {batch && (
          <>
            {/* Progress */}
            <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500 transition-all"
                style={{ width: total ? `${Math.round((done / total) * 100)}%` : '0%' }}
              />
            </div>

            {/* Counts */}
            <div className="grid grid-cols-4 gap-2 text-center" data-testid="reminder-batch-counts">
              {[
                { label: 'Sent', value: counts.sent || 0, icon: CheckCircle2, cls: 'text-emerald-600' },
                { label: 'Skipped', value: counts.skipped || 0, icon: MinusCircle, cls: 'text-gray-500' },
                { label: 'Failed', value: counts.failed || 0, icon: XCircle, cls: 'text-red-500' },
                { label: 'Pending', value: Math.max(0, total - done), icon: Clock, cls: 'text-blue-500' },
              ].map(({ label, value, icon: Icon, cls }) => (
                <div key={label} className="bg-gray-50 rounded-lg py-2">
                  <Icon className={`h-4 w-4 mx-auto ${cls}`} />
                  <p className="text-lg font-bold text-gray-900">{value}</p>
                  <p className="text-[10px] text-gray-500">{label}</p>
                </div>
              ))}
            </div>

            {/* Failures */}
            {failed.length > 0 && (
              <div className="space-y-1 max-h-40 overflow-y-auto" data-testid="reminder-batch-failures">
                <p className="text-xs font-semibold text-red-600">Failed messages</p>
                {failed.map((m) => (
                  <div key={m.id} className="text-xs bg-red-50 border border-red-100 rounded px-2 py-1">
                    <span className="font-medium">{m.clientName || m.taskId}</span>
                    <span className="text-red-600"> — {m.error || 'unknown error'}</span>
                  </div>
                ))}
                {!active && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleRetryFailed}
                    disabled={retrying}
                    className="w-full mt-1"
                    data-testid="reminder-batch-retry-btn"
                  >
                    <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${retrying ? 'animate-spin' : ''}`} />
                    Retry failed messages
                  </Button>
                )}
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReminderBatchPanel;
